import React, { useState } from 'react';
import { Search } from 'lucide-react';

const ProductSelectionModal = ({ 
    show, 
    onClose, 
    products, 
    quantities,
    onQuantityChange,
    onSave,
    title,
    isEditMode
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Filter products based on search term
    const filteredProducts = products.filter(product => 
        product.proVarietal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.proFanciful?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.proVineyard?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.proVintage?.toString().includes(searchTerm)
    );

    // Calculate pagination
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{title}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            />
                        </div>
                        <div className="modal-body">
                            {/* Search Bar */}
                            <div className="mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Products Table */}
                            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                        <tr>
                                            <th>Vintage</th>
                                            <th>Brand</th>
                                            <th>Varietal</th>
                                            <th>Fanciful Name</th>
                                            <th>Vineyard</th>
                                            <th>Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {(isEditMode ? products : paginatedProducts).map((product) => (
                                        <tr key={product.productID}>
                                            <td>{product.proVintage === 0 || product.proVintage === '0' ? "N/V" : product.proVintage}</td>
                                            <td>{product.proBrand || 'N/A'}</td>
                                            <td>{product.proVarietal || 'N/A'}</td>
                                            <td>{product.proFanciful || 'N/A'}</td>
                                            <td>{product.proVineyard || 'N/A'}</td>
                                            <td style={{ width: '150px' }}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={quantities[product.productID] || 0}
                                                    onChange={(e) => {
                                                        const value = Math.max(0, parseInt(e.target.value) || 0);
                                                        onQuantityChange(product.productID, value);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <nav className="mt-3">
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            >
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, idx) => (
                                            <li
                                                key={idx}
                                                className={`page-item ${currentPage === idx + 1 ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(idx + 1)}
                                                >
                                                    {idx + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            >
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onSave}
                            >
                                Add Selected Products
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProductSelectionModal;